import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { useGetLegalClientFullObject } from '../../hooks/reactQuerys/LegalClientQueries';
import { formatGermanDate } from '../i18n';

interface Props {
  idLegalClient: number;
  /** Die aus dem Dialog zu übernehmenden Felder (Ergebnis der Feld-Auswahl). */
  mergeData: Record<string, unknown>;
  onConfirm: () => void;
  onBack: () => void;
}

interface PreviewRow {
  label: string;
  value: string;
  fromDialog: boolean;
}

const fmtAddress = (a?: {
  Street?: string | null;
  StreetNr?: string | null;
  Number?: string | null;
  Postcode?: string | null;
  City?: string | null;
}): string => {
  if (!a) return '';
  const street = [a.Street, a.StreetNr ?? a.Number].filter(Boolean).join(' ');
  const city = [a.Postcode, a.City].filter(Boolean).join(' ');
  return [street, city].filter(Boolean).join(', ').trim();
};

/**
 * Gesamtvorschau eines zusammengeführten Mandanten (Ticket #1024): lädt den
 * vollständigen Bestands-Mandanten und legt die aus dem Dialog gewählten Werte
 * darüber — so wie der Datensatz nach dem Bestätigen abgelegt wird.
 */
export const MergeClientPreviewDialog: React.FC<Props> = ({
  idLegalClient,
  mergeData,
  onConfirm,
  onBack,
}) => {
  const { legalClientFullObject, isFetchingLegalClientFullObject } =
    useGetLegalClientFullObject(idLegalClient);

  const rows = useMemo<PreviewRow[]>(() => {
    const lc = legalClientFullObject as unknown as
      | Record<string, unknown>
      | undefined;
    if (!lc) return [];

    const scalar = (key: string, label: string, isDate = false): PreviewRow => {
      const fromDialog = mergeData[key] !== undefined;
      const raw = fromDialog ? mergeData[key] : lc[key];
      const str = (raw ?? '').toString().trim();
      return {
        label,
        value: isDate && str ? formatGermanDate(str) : str || '–',
        fromDialog,
      };
    };

    const result: PreviewRow[] = [
      scalar('FirstName', 'Vorname'),
      scalar('LastName', 'Nachname'),
      scalar('SurName', 'Geburtsname'),
      scalar('Email', 'E-Mail'),
      scalar('Birthdate', 'Geburtsdatum', true),
      scalar('Birthplace', 'Geburtsort'),
      scalar('TaxNumber', 'Steuer-Nr.'),
      scalar('RegisterNumber', 'Handelsregister-Nr.'),
    ];

    // Telefon: bestehende ContactInformation (Typ 1) + ggf. neue aus Dialog.
    const contactArray =
      (lc.ContactInformationArray as
        | Array<{ Contact?: string; idContactInformationType?: number }>
        | undefined) ?? [];
    const phones = contactArray
      .filter(c => c.idContactInformationType === 1 && c.Contact)
      .map(c => (c.Contact as string).trim());
    const dialogPhone = (mergeData.PhoneNumber as string | undefined)?.trim();
    let phoneFromDialog = false;
    if (dialogPhone && !phones.includes(dialogPhone)) {
      phones.push(dialogPhone);
      phoneFromDialog = true;
    }
    result.push({
      label: 'Telefon',
      value: phones.join(', ') || '–',
      fromDialog: phoneFromDialog,
    });

    // Anschrift: bestehende Adresse + ggf. neue aus Dialog.
    const addrArray =
      (lc.AddressArray as Array<Record<string, unknown>> | undefined) ?? [];
    const existingAddrs = addrArray.map(a => fmtAddress(a)).filter(Boolean);
    const dialogAddr = mergeData.address as
      | { Street?: string; StreetNr?: string; Postcode?: string; City?: string }
      | undefined;
    const dialogAddrStr = dialogAddr ? fmtAddress(dialogAddr) : '';
    let addrFromDialog = false;
    if (dialogAddrStr && !existingAddrs.includes(dialogAddrStr)) {
      existingAddrs.push(dialogAddrStr);
      addrFromDialog = true;
    }
    result.push({
      label: 'Anschrift',
      value: existingAddrs.join(' · ') || '–',
      fromDialog: addrFromDialog,
    });

    return result;
  }, [legalClientFullObject, mergeData]);

  return (
    <Dialog open onClose={onBack} maxWidth='sm' fullWidth>
      <DialogTitle>Vorschau: zusammengeführter Mandant</DialogTitle>
      <DialogContent>
        {isFetchingLegalClientFullObject ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !legalClientFullObject ? (
          <Alert severity='error'>
            Mandant konnte nicht geladen werden (Nr. {idLegalClient}).
          </Alert>
        ) : (
          <>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              So wird der Mandant nach dem Zusammenführen gespeichert.
              <Chip
                size='small'
                label='aus Dialog'
                color='info'
                sx={{ ml: 1 }}
              />{' '}
              markiert übernommene/ergänzte Angaben.
            </Typography>
            <Table size='small'>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.label}>
                    <TableCell
                      sx={{ color: 'text.secondary', width: 170, border: 0 }}
                    >
                      {r.label}
                    </TableCell>
                    <TableCell sx={{ border: 0 }}>
                      {r.value}
                      {r.fromDialog && r.value !== '–' && (
                        <Chip
                          size='small'
                          label='aus Dialog'
                          color='info'
                          variant='outlined'
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack}>Zurück</Button>
        <Button
          variant='contained'
          onClick={onConfirm}
          disabled={isFetchingLegalClientFullObject || !legalClientFullObject}
        >
          Endgültig übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
